import { useState, useEffect } from 'react';
import {
  Activity,
  Server,
  Database,
  Wifi,
  Monitor,
  CreditCard,
  Shield,
  Clock,
  Bell,
  Settings,
  FileText,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  XCircle,
  ArrowUp,
  ArrowDown,
  Zap,
  HardDrive,
  Users,
  TrendingUp,
  Play,
  Pause,
  Power,
  Download,
  ChevronRight,
  ExternalLink,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import useAuthStore from '../stores/authStore';
import api from '../api/client';


// ============================================
// HELPER COMPONENTS
// ============================================
const StatusBadge = ({ status }) => {
  const config = {
    healthy: { label: 'Healthy', color: 'success', Icon: CheckCircle },
    degraded: { label: 'Degraded', color: 'warning', Icon: AlertTriangle },
    incident: { label: 'Incident', color: 'danger', Icon: XCircle },
    warning: { label: 'Warning', color: 'warning', Icon: AlertTriangle },
    critical: { label: 'Critical', color: 'danger', Icon: AlertCircle },
    info: { label: 'Info', color: 'info', Icon: Bell },
  };
  const { label, color, Icon } = config[status] || config.healthy;
  return (
    <span className={`status-badge status-badge--${color}`}>
      <Icon size={14} />
      {label}
    </span>
  );
};

// null/undefined means the backend has no source for this figure yet (see
// internal_health.py). Render that as "Soon" rather than 0 — a zero here reads
// as a measurement, and someone will eventually act on it.
const NO_DATA = Symbol('no-data');
const hasValue = (v) => v !== null && v !== undefined;
const orSoon = (v) => (hasValue(v) ? v : NO_DATA);

const MetricCard = ({ icon: Icon, label, value, unit, trend, trendValue, status, hint }) => {
  const missing = value === NO_DATA || !hasValue(value);
  return (
    <div className={`metric-card ${!missing && status ? `metric-card--${status}` : ''} ${missing ? 'metric-card--pending' : ''}`}>
      <div className="metric-card__header">
        <div className="metric-card__icon">
          <Icon size={18} />
        </div>
        <span className="metric-card__label">{label}</span>
      </div>
      <div className="metric-card__body">
        {missing ? (
          <span className="metric-card__soon" title="Not measured yet — no data source for this figure">
            Soon
          </span>
        ) : (
          <>
            <span className="metric-card__value">{value}</span>
            {unit && <span className="metric-card__unit">{unit}</span>}
          </>
        )}
      </div>
      {!missing && (trend || hint) && (
        <div className="metric-card__footer">
          {trend && (
            <span className={`metric-card__trend metric-card__trend--${trend}`}>
              {trend === 'up' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
              {trendValue}
            </span>
          )}
          {hint && <span className="metric-card__hint">{hint}</span>}
        </div>
      )}
    </div>
  );
};

const GlobalMetric = ({ label, value, suffix = '' }) => (
  <div className="global-metric">
    <span className={`global-metric__value ${hasValue(value) ? '' : 'global-metric__value--soon'}`}>
      {hasValue(value) ? `${value}${suffix}` : 'Soon'}
    </span>
    <span className="global-metric__label">{label}</span>
  </div>
);

const Section = ({ icon: Icon, title, badge, children, collapsible = false }) => {
  const [isOpen, setIsOpen] = useState(true);
  return (
    <section className="health-section">
      <div className="health-section__header" onClick={collapsible ? () => setIsOpen(!isOpen) : undefined}>
        <div className="health-section__title-group">
          <Icon size={20} className="health-section__icon" />
          <h2 className="health-section__title">{title}</h2>
          {badge && <span className="health-section__badge">{badge}</span>}
        </div>
        {collapsible && (
          <ChevronRight size={18} className={`health-section__chevron ${isOpen ? 'rotated' : ''}`} />
        )}
      </div>
      {isOpen && <div className="health-section__content">{children}</div>}
    </section>
  );
};

const formatTimeAgo = (timestamp) => {
  const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

// Audit actions worth colouring differently on the timeline. Anything not
// listed falls back to a plain "action" dot.
const AUDIT_TYPE = {
  system_restart: 'deploy',
  maintenance_mode: 'alert',
  pause_deployments: 'alert',
  disable_commands: 'alert',
  force_handshake: 'action',
  account_locked: 'alert',
  login_failed: 'alert',
  login_denied: 'alert',
  cafe_suspend: 'alert',
  cafe_reactivate: 'resolve',
};

// Alerts are DERIVED from the measured numbers above — no alerting backend
// exists, and inventing a feed would be the same lie this page just removed.
// Every rule below fires only on a value that was actually measured.
function deriveAlerts(d) {
  const out = [];
  const push = (severity, title, category) => out.push({ id: `${category}:${title}`, severity, title, category });

  if (hasValue(d.api.errorRate5xx) && d.api.errorRate5xx >= 1) {
    push(d.api.errorRate5xx >= 5 ? 'critical' : 'warning', `Server error rate at ${d.api.errorRate5xx}% (5xx)`, 'api');
  }
  if (hasValue(d.database.poolUsage) && hasValue(d.database.poolMax) && d.database.poolMax > 0) {
    const pct = (d.database.poolUsage / d.database.poolMax) * 100;
    if (pct >= 70) push(pct >= 90 ? 'critical' : 'warning', `DB connection pool ${Math.round(pct)}% used`, 'database');
  }
  if (hasValue(d.database.diskPercent) && d.database.diskPercent >= 80) {
    push(d.database.diskPercent >= 90 ? 'critical' : 'warning', `Disk ${d.database.diskPercent}% full`, 'database');
  }
  if (hasValue(d.database.slowQueries) && d.database.slowQueries > 0) {
    push('warning', `${d.database.slowQueries} slow quer${d.database.slowQueries === 1 ? 'y' : 'ies'} running now`, 'database');
  }
  if (hasValue(d.network.cmdStuck) && d.network.cmdStuck > 0) {
    push('warning', `${d.network.cmdStuck} kiosk command(s) never acknowledged`, 'network');
  }
  // A stale snapshot must not read as "all clear" — the sweep is the only
  // source for command health, so its silence is itself the alert.
  if (hasValue(d.network.snapshotAge) && d.network.snapshotAge > 300) {
    push('warning', `Command-health sweep is ${Math.round(d.network.snapshotAge / 60)}m stale`, 'network');
  }
  if (d.security.failedLoginsRate !== 'normal') {
    push(d.security.failedLoginsRate === 'critical' ? 'critical' : 'warning', `${d.security.failedLogins24h} failed logins in 24h`, 'security');
  }
  if (d.security.lockouts24h > 0) push('warning', `${d.security.lockouts24h} account lockout(s) in 24h`, 'security');
  if (d.fleet.stale > 0) push('warning', `${d.fleet.stale} PC(s) not seen in over 24h`, 'fleet');
  if (d.licensing.expiring7d > 0) push('warning', `${d.licensing.expiring7d} licence(s) expiring within 7 days`, 'licensing');
  if (hasValue(d.jobs.queueDepth) && d.jobs.queueDepth > 100) {
    push('warning', `Background job queue backed up (${d.jobs.queueDepth})`, 'jobs');
  }
  if (d.jobs.workers === 0) push('critical', 'No Celery workers responding', 'jobs');
  if (d.systemState?.maintenance_mode) push('info', 'Maintenance mode is ON', 'system');
  if (d.systemState?.commands_disabled) push('info', 'Kiosk commands are disabled', 'system');
  if (d.systemState?.deployments_paused) push('info', 'Deployments are paused', 'system');
  return out;
}

// ============================================
// MAIN COMPONENT
// ============================================
export default function SystemHealth() {
  const { canActAsSuperAdmin } = useAuthStore();
  const navigate = useNavigate();
  const [data, setData] = useState({
    global: { status: 'healthy', uptimeHours: null, uptime30d: null, activeIncidents: null, lastDeployment: null, mttr: null },
    api: { availability: null, avgLatency: null, p95Latency: null, p99Latency: null, errorRate4xx: null, errorRate5xx: null, rps: null, totalRequests: 0, services: [] },
    database: { poolUsage: null, poolMax: null, readLatency: null, writeLatency: null, slowQueries: null, replicationLag: null, diskPercent: null, diskTotal: null, diskUsed: null, backupStatus: 'unknown', lastBackup: null },
    network: { activeConnections: null, cmdSuccessRate: null, cmdSucceeded: null, cmdFailed: null, cmdStuck: null, avgRtt: null, commandAckTime: null, snapshotAge: null },
    fleet: { total: 0, online: 0, offline: 0, stale: 0, versions: {}, osList: null, staleMonthPcs: [], topUsagePcs: [], onlineHistory: [] },
    licensing: { active: 0, total: 0, expiring7d: 0, suspended: 0, violations: null, trialConversionRate: null, mismatches: null },
    security: { failedLogins24h: 0, failedLoginsRate: 'normal', loginDenied24h: 0, twofaFailures24h: 0, lockouts24h: 0, otpFailures: null, tokenAnomalies: null, integrityOk: null },
    jobs: { queueDepth: null, processing: null, failed24h: null, longRunning: null, queues: [], workers: null },
    alerts: [],
    insights: { baselineReady: false, points: 0, pointsNeeded: null, items: [] },
    auditTrail: [],
    systemState: { maintenance_mode: false, deployments_paused: false, commands_disabled: false },
  });
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [actionLoading, setActionLoading] = useState(null);
  const [showConfirm, setShowConfirm] = useState(null);

  // Fetch all health data from API
  const fetchHealthData = async () => {
    setIsLoading(true);
    try {
      // A failed call yields {} so every field falls through to null ("Soon"),
      // never to a plausible-looking zero.
      const nil = () => ({ data: {} });
      const [overview, apiHealth, dbHealth, networkHealth, fleetHealth, fleetHistory, licensingHealth, securityHealth, jobsHealth, insightsRes, systemState, auditRes] = await Promise.all([
        api.get('/internal/health/overview').catch(nil),
        api.get('/internal/health/api').catch(nil),
        api.get('/internal/health/database').catch(nil),
        api.get('/internal/health/network').catch(nil),
        api.get('/internal/health/fleet').catch(nil),
        api.get('/internal/fleet/online-history', { params: { days: 14 } }).catch(nil),
        api.get('/internal/health/licensing').catch(nil),
        api.get('/internal/health/security').catch(nil),
        api.get('/internal/health/jobs').catch(nil),
        api.get('/internal/health/insights').catch(nil),
        api.get('/internal/system/state').catch(() => ({ data: { maintenance_mode: false, deployments_paused: false, commands_disabled: false } })),
        api.get('/audit/', { params: { limit: 12 } }).catch(() => ({ data: [] })),
      ]);

      const overviewData = overview.data || {};
      const apiData = apiHealth.data || {};
      const dbData = dbHealth.data || {};
      const netData = networkHealth.data || {};
      const fleetData = fleetHealth.data || {};
      const fleetHistoryData = fleetHistory.data || {};
      const licData = licensingHealth.data || {};
      const secData = securityHealth.data || {};
      const jobData = jobsHealth.data || {};
      const insightData = insightsRes.data || {};

      const next = {
        global: {
          status: overviewData.status || 'healthy',
          // How long the backend process has been running, continuously,
          // right now — real and already measured (see internal_health.py's
          // _PROCESS_STARTED_AT), unlike the 24h/7d/30d rolling percentages
          // this replaces: those need a history of periodic health checks
          // that nothing records yet, so they were always "Soon" no matter
          // how long the page existed.
          uptimeHours: overviewData.process_uptime_seconds != null
            ? Math.round(overviewData.process_uptime_seconds / 3600 * 10) / 10
            : null,
          uptime30d: overviewData.uptime_percent ?? null,
          activeIncidents: overviewData.active_incidents ?? null,
          lastDeployment: overviewData.last_deployment ?? null,
          mttr: overviewData.mttr_hours ?? null,
        },
        api: {
          availability: apiData.availability_percent ?? null,
          avgLatency: apiData.avg_latency_ms ?? null,
          p95Latency: apiData.p95_latency_ms ?? null,
          p99Latency: apiData.p99_latency_ms ?? null,
          errorRate4xx: apiData.error_rate_4xx_percent ?? null,
          errorRate5xx: apiData.error_rate_5xx_percent ?? null,
          rps: apiData.requests_per_second ?? null,
          totalRequests: apiData.total_requests ?? 0,
          services: apiData.services || [],
        },
        database: {
          poolUsage: dbData.connection_pool_usage ?? null,
          poolMax: dbData.connection_pool_max ?? null,
          readLatency: dbData.read_latency_ms ?? null,
          writeLatency: dbData.write_latency_ms ?? null,
          slowQueries: dbData.slow_queries ?? null,
          replicationLag: dbData.replication_lag_seconds ?? null,
          diskPercent: dbData.disk_utilization_percent ?? null,
          diskTotal: dbData.disk_total ?? null,
          diskUsed: dbData.disk_used ?? null,
          backupStatus: dbData.backup_status || 'unknown',
          lastBackup: dbData.last_backup ?? null,
        },
        network: {
          activeConnections: netData.websocket_connections ?? null,
          cmdSuccessRate: netData.command_success_rate ?? null,
          cmdSucceeded: netData.commands_succeeded ?? null,
          cmdFailed: netData.commands_failed ?? null,
          cmdStuck: netData.commands_stuck ?? null,
          avgRtt: netData.avg_rtt_ms ?? null,
          commandAckTime: netData.command_ack_time_ms ?? null,
          snapshotAge: netData.snapshot_age_seconds ?? null,
        },
        fleet: {
          total: fleetData.total_pcs ?? 0,
          online: fleetData.online_pcs ?? 0,
          offline: fleetData.offline_pcs ?? 0,
          stale: fleetData.stale_pcs ?? 0,
          versions: (fleetData.agent_versions || []).reduce((acc, v) => ({ ...acc, [v.version]: v.count }), {}),
          osList: fleetData.os_distribution
            ? fleetData.os_distribution.reduce((acc, o) => ({ ...acc, [o.os]: o.count }), {})
            : null,
          staleMonthPcs: fleetData.stale_month_pcs || [],
          topUsagePcs: fleetData.top_usage_pcs || [],
          onlineHistory: (fleetHistoryData.days || []).map((pt) => ({
            date: pt.date,
            label: new Date(`${pt.date}T00:00:00Z`).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC',
            }),
            peakOnline: pt.peak_online_pcs,
          })),
        },
        licensing: {
          active: licData.active_licenses ?? 0,
          total: (licData.active_licenses ?? 0) + (licData.expired ?? 0) + (licData.suspended ?? 0),
          expiring7d: licData.expiring_soon ?? 0,
          suspended: licData.suspended ?? 0,
          violations: licData.violations ?? null,
          trialConversionRate: licData.trial_conversion_percent ?? null,
          mismatches: licData.pc_mismatches ?? null,
        },
        security: {
          failedLogins24h: secData.failed_logins_24h ?? 0,
          failedLoginsRate: (secData.failed_logins_24h ?? 0) > 50 ? 'critical' : (secData.failed_logins_24h ?? 0) > 20 ? 'elevated' : 'normal',
          loginDenied24h: secData.login_denied_24h ?? 0,
          twofaFailures24h: secData.twofa_failures_24h ?? 0,
          lockouts24h: secData.account_lockouts_24h ?? 0,
          otpFailures: secData.otp_failures_24h ?? null,
          tokenAnomalies: secData.token_anomalies ?? null,
          integrityOk: secData.integrity_issues == null ? null : secData.integrity_issues === 0,
        },
        jobs: {
          queueDepth: jobData.queue_depth ?? null,
          processing: jobData.processing ?? null,
          failed24h: jobData.failed_jobs_24h ?? null,
          longRunning: jobData.long_running_tasks ?? null,
          queues: jobData.queues || [],
          workers: jobData.workers ?? null,
        },
        insights: {
          baselineReady: Boolean(insightData.baseline_ready),
          points: insightData.points ?? 0,
          pointsNeeded: insightData.points_needed ?? null,
          items: insightData.insights || [],
        },
        auditTrail: (auditRes.data || []).map((row) => ({
          id: row.id,
          type: AUDIT_TYPE[row.action] || 'action',
          message: row.detail || row.action,
          time: row.timestamp,
          user: row.user_email || row.user_id || null,
        })),
        systemState: systemState.data,
      };
      next.alerts = deriveAlerts(next);
      setData(next);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch health data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchHealthData();
  };

  const exportAuditCsv = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = data.auditTrail.map((e) => [e.time, e.type, e.message, e.user].map(esc).join(','));
    const csv = ['timestamp,type,message,user', ...rows].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `system-health-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Initial fetch and periodic refresh
  useEffect(() => {
    fetchHealthData();
    const interval = setInterval(fetchHealthData, 30000);
    return () => clearInterval(interval);
  }, []);

  // System action handlers
  const executeSystemAction = async (action, endpoint) => {
    setActionLoading(action);
    try {
      const response = await api.post(`/internal/system/${endpoint}`);
      if (response.data.success) {
        alert(`${action} executed successfully!`);
        fetchHealthData(); // Refresh state
      }
    } catch (err) {
      alert(`Failed to execute ${action}: ${err.response?.data?.detail || err.message}`);
    } finally {
      setActionLoading(null);
      setShowConfirm(null);
    }
  };

  const handleMaintenanceMode = async () => {
    setActionLoading('maintenance');
    try {
      const response = await api.post('/internal/system/maintenance-mode', {
        enabled: !data.systemState.maintenance_mode,
      });
      if (response.data.success) {
        alert(response.data.message);
        fetchHealthData();
      }
    } catch (err) {
      alert(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setActionLoading(null);
      setShowConfirm(null);
    }
  };

  return (
    <div className="system-health">
      {/* Page Header */}
      <header className="page-header">
        <div className="page-header-content">
          <div className="page-header-icon">
            <Activity size={24} />
          </div>
          <div>
            <h1 className="page-title">System Health</h1>
            <p className="page-subtitle">
              Last updated {formatTimeAgo(lastRefresh)}
            </p>
          </div>
        </div>
        <div className="page-header-actions">
          <button className="btn btn--ghost" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw size={16} className={isLoading ? 'spin' : ''} />
            Refresh
          </button>
        </div>
      </header>

      {/* Section 1: Global Platform Health */}
      <section className="global-health">
        <div className="global-health__status">
          <div className={`global-status-badge global-status-badge--${data.global.status}`}>
            {data.global.status === 'healthy' && <CheckCircle size={28} />}
            {data.global.status === 'degraded' && <AlertTriangle size={28} />}
            {data.global.status === 'incident' && <XCircle size={28} />}
            <span>{data.global.status.toUpperCase()}</span>
          </div>
          <div className="global-health__live-indicator">
            <span className="live-dot"></span>
            <span>Live</span>
          </div>
        </div>
        <div className="global-health__metrics">
          <GlobalMetric label="Uptime" value={data.global.uptimeHours} suffix="h" />
          <GlobalMetric label="Uptime 30d" value={data.global.uptime30d} suffix="%" />
          <GlobalMetric label="Active Incidents" value={data.global.activeIncidents} />
          <GlobalMetric
            label="Last Deploy"
            value={data.global.lastDeployment ? formatTimeAgo(data.global.lastDeployment) : null}
          />
          <GlobalMetric label="MTTR" value={data.global.mttr} suffix="m" />
        </div>
      </section>

      <div className="health-grid">
        {/* Section 2: Backend & API Health */}
        <Section icon={Server} title="Backend & API Health">
          <div className="metrics-grid">
            <MetricCard
              icon={Zap}
              label="API Availability"
              value={orSoon(data.api.availability)}
              unit="%"
              hint="non-5xx share"
              status={data.api.availability >= 99.9 ? 'success' : data.api.availability >= 99 ? 'warning' : 'danger'}
            />
            <MetricCard
              icon={Clock}
              label="P95 Latency"
              value={orSoon(data.api.p95Latency)}
              unit="ms"
              hint={hasValue(data.api.avgLatency) ? `avg ${data.api.avgLatency}ms` : null}
              status={data.api.p95Latency < 150 ? 'success' : data.api.p95Latency < 300 ? 'warning' : 'danger'}
            />
            <MetricCard
              icon={AlertCircle}
              label="Error Rate"
              value={hasValue(data.api.errorRate4xx) && hasValue(data.api.errorRate5xx)
                ? (data.api.errorRate4xx + data.api.errorRate5xx).toFixed(2)
                : NO_DATA}
              unit="%"
              hint={hasValue(data.api.errorRate5xx) ? `${data.api.errorRate5xx}% 5xx` : null}
              status={data.api.errorRate5xx < 0.5 ? 'success' : 'danger'}
            />
            <MetricCard
              icon={TrendingUp}
              label="Requests/sec"
              value={orSoon(data.api.rps)}
              hint={data.api.totalRequests ? `${data.api.totalRequests.toLocaleString()} total` : null}
            />
          </div>
          <div className="services-list">
            <h4 className="subsection-title">Service Status</h4>
            {data.api.services.length === 0 ? (
              <div className="empty-state">No traffic recorded yet</div>
            ) : data.api.services.map((svc) => (
              <div key={svc.name} className="service-row">
                <div className="service-row__status">
                  <span className={`status-dot status-dot--${svc.status === 'healthy' ? 'online' : 'warning'}`} />
                  <span className="service-row__name">{svc.name}</span>
                </div>
                <div className="service-row__meta">
                  <span className="service-row__latency">{hasValue(svc.latency_ms) ? `${svc.latency_ms}ms` : '—'}</span>
                  <span className="service-row__heartbeat">{svc.requests?.toLocaleString()} reqs · {svc.error_percent}% err</span>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Section 3: Database & Storage Health */}
        <Section icon={Database} title="Database & Storage">
          <div className="metrics-grid">
            <MetricCard
              icon={Activity}
              label="Connection Pool"
              value={hasValue(data.database.poolUsage) && hasValue(data.database.poolMax)
                ? `${data.database.poolUsage}/${data.database.poolMax}`
                : NO_DATA}
              hint="global engine"
              status={data.database.poolUsage / data.database.poolMax < 0.7 ? 'success' : data.database.poolUsage / data.database.poolMax < 0.9 ? 'warning' : 'danger'}
            />
            <MetricCard
              icon={Clock}
              label="Read Latency"
              value={orSoon(data.database.readLatency)}
              unit="ms"
              status={data.database.readLatency < 10 ? 'success' : data.database.readLatency < 50 ? 'warning' : 'danger'}
            />
            <MetricCard
              icon={HardDrive}
              label="Disk Usage"
              value={orSoon(data.database.diskPercent)}
              unit="%"
              hint={data.database.diskUsed ? `${data.database.diskUsed} / ${data.database.diskTotal}` : null}
              status={data.database.diskPercent < 70 ? 'success' : data.database.diskPercent < 85 ? 'warning' : 'danger'}
            />
            <MetricCard
              icon={AlertTriangle}
              label="Slow Queries"
              value={orSoon(data.database.slowQueries)}
              hint="running now"
              status={data.database.slowQueries === 0 ? 'success' : data.database.slowQueries < 5 ? 'warning' : 'danger'}
            />
          </div>
          <div className="metrics-grid" style={{ marginTop: 'var(--space-4)' }}>
            <MetricCard
              icon={Clock}
              label="Write Latency"
              value={orSoon(data.database.writeLatency)}
              unit="ms"
            />
            <MetricCard
              icon={Database}
              label="Replication Lag"
              value={orSoon(data.database.replicationLag)}
              unit="s"
            />
          </div>
          <div className="backup-status">
            <div className={`backup-badge backup-badge--${data.database.backupStatus}`}>
              {data.database.backupStatus === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
              <span>
                Backup: {data.database.lastBackup ? formatTimeAgo(data.database.lastBackup) : 'Soon'}
              </span>
            </div>
          </div>
        </Section>

        {/* Section 4: Network & Connectivity */}
        <Section icon={Wifi} title="Network & Connectivity">
          <div className="metrics-grid">
            <MetricCard
              icon={Users}
              label="Active Connections"
              value={hasValue(data.network.activeConnections) ? data.network.activeConnections.toLocaleString() : NO_DATA}
              hint="live websockets"
            />
            <MetricCard
              icon={CheckCircle}
              label="Command Success"
              value={orSoon(data.network.cmdSuccessRate)}
              unit="%"
              hint={hasValue(data.network.cmdSucceeded)
                ? `${data.network.cmdSucceeded} ok / ${data.network.cmdFailed} failed`
                : null}
              status={data.network.cmdSuccessRate >= 99.5 ? 'success' : data.network.cmdSuccessRate >= 98 ? 'warning' : 'danger'}
            />
            <MetricCard
              icon={Zap}
              label="Avg RTT"
              value={orSoon(data.network.avgRtt)}
              unit="ms"
            />
            <MetricCard
              icon={Clock}
              label="Cmd Ack Time"
              value={orSoon(data.network.commandAckTime)}
              unit="ms"
            />
          </div>
        </Section>

        {/* Section 5: Client PC Fleet Health */}
        <Section icon={Monitor} title="PC Fleet Health">
          <div className="fleet-overview">
            <div className="fleet-stat fleet-stat--total">
              <span className="fleet-stat__value">{data.fleet.total}</span>
              <span className="fleet-stat__label">Total PCs</span>
            </div>
            <div className="fleet-stat fleet-stat--online">
              <span className="fleet-stat__value">{data.fleet.online}</span>
              <span className="fleet-stat__label">Online</span>
              <span className="fleet-stat__pct">
                {data.fleet.total ? `${((data.fleet.online / data.fleet.total) * 100).toFixed(1)}%` : '—'}
              </span>
            </div>
            <div className="fleet-stat fleet-stat--offline">
              <span className="fleet-stat__value">{data.fleet.offline}</span>
              <span className="fleet-stat__label">Offline</span>
            </div>
            <div className="fleet-stat fleet-stat--stale">
              <span className="fleet-stat__value">{data.fleet.stale}</span>
              <span className="fleet-stat__label">Stale (&gt;24h)</span>
            </div>
          </div>
          <div className="fleet-history-card">
            <div className="fleet-history-card__header">
              <h4 className="distribution-card__title">PCs Online — Daily Peak</h4>
              <span className="fleet-history-card__subtitle">
                Last 14 days · peak concurrent PCs with an active session each day
              </span>
            </div>
            {data.fleet.onlineHistory.length === 0 ? (
              <div className="empty-state">No session history in this window</div>
            ) : (
              <ResponsiveContainer width="100%" height={340}>
                <AreaChart data={data.fleet.onlineHistory} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="fleetOnlineGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22c55e" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 12 }}
                  />
                  <YAxis
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'rgba(26,26,36,0.95)',
                      backdropFilter: 'blur(16px)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                      fontSize: '13px',
                      color: 'white',
                    }}
                    formatter={(value) => [`${value} PC${value === 1 ? '' : 's'}`, 'Peak online']}
                  />
                  <Area
                    type="monotone"
                    dataKey="peakOnline"
                    stroke="#22c55e"
                    strokeWidth={2.5}
                    fill="url(#fleetOnlineGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="distribution-grid">
            <div className="distribution-card">
              <h4 className="distribution-card__title">Agent Versions</h4>
              {Object.keys(data.fleet.versions).length === 0 ? (
                <div className="empty-state">No PCs registered</div>
              ) : Object.entries(data.fleet.versions).map(([ver, count]) => (
                <div key={ver} className="distribution-row">
                  <span className="distribution-row__label">{ver === 'unknown' ? 'unknown' : `v${ver}`}</span>
                  <div className="distribution-row__bar">
                    <div style={{ width: `${(count / data.fleet.total) * 100}%` }} />
                  </div>
                  <span className="distribution-row__value">{count}</span>
                </div>
              ))}
            </div>
            <div className="distribution-card">
              <h4 className="distribution-card__title">OS Distribution</h4>
              {!data.fleet.osList ? (
                <div className="empty-state" title="Kiosks do not report their OS yet">Soon</div>
              ) : Object.entries(data.fleet.osList).map(([os, count]) => (
                <div key={os} className="distribution-row">
                  <span className="distribution-row__label">{os}</span>
                  <div className="distribution-row__bar">
                    <div style={{ width: `${(count / data.fleet.total) * 100}%` }} />
                  </div>
                  <span className="distribution-row__value">{count}</span>
                </div>
              ))}
            </div>
            <div className="distribution-card">
              <h4 className="distribution-card__title">Offline 30+ Days</h4>
              {data.fleet.staleMonthPcs.length === 0 ? (
                <div className="empty-state">No PCs offline that long</div>
              ) : (
                <div className="stale-pc-list">
                  {data.fleet.staleMonthPcs.map((pc) => (
                    <div
                      key={`${pc.cafe_id}-${pc.id}`}
                      className="distribution-row"
                      title={`${pc.name || `PC #${pc.id}`} — ${pc.cafe_name || 'unknown café'}${pc.last_seen ? ` — last seen ${new Date(pc.last_seen).toLocaleString()}` : ' — never seen'}`}
                    >
                      <span className="distribution-row__label distribution-row__label--wide">
                        {`#${pc.id} ${pc.name || ''}`}
                      </span>
                      <span className="distribution-row__value distribution-row__value--wide">
                        {pc.last_seen ? formatTimeAgo(pc.last_seen) : 'Never'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="distribution-card">
              <h4 className="distribution-card__title">Peak Usage (7d logins)</h4>
              {data.fleet.topUsagePcs.length === 0 ? (
                <div className="empty-state">No PCs registered</div>
              ) : (
                <div className="stale-pc-list">
                  {(() => {
                    const maxCount = data.fleet.topUsagePcs[0]?.session_count_7d || 1;
                    return data.fleet.topUsagePcs.map((pc) => (
                      <div
                        key={`${pc.cafe_id}-${pc.id}`}
                        className="distribution-row"
                        title={`${pc.name || `PC #${pc.id}`} — ${pc.cafe_name || 'unknown café'} — ${pc.session_count_7d} login(s) in the last 7 days`}
                      >
                        <span className="distribution-row__label distribution-row__label--wide">
                          {`#${pc.id} ${pc.name || ''}`}
                        </span>
                        <div className="distribution-row__bar">
                          <div style={{ width: `${(pc.session_count_7d / maxCount) * 100}%` }} />
                        </div>
                        <span className="distribution-row__value">{pc.session_count_7d}</span>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          </div>
        </Section>

        {/* Section 6: Licensing & Subscription Health */}
        <Section icon={CreditCard} title="Licensing Health">
          <div className="metrics-grid">
            <MetricCard
              icon={CheckCircle}
              label="Active Licenses"
              value={data.licensing.active}
              unit={`/ ${data.licensing.total}`}
            />
            <MetricCard
              icon={Clock}
              label="Expiring (7d)"
              value={data.licensing.expiring7d}
              status={data.licensing.expiring7d > 0 ? 'warning' : 'success'}
            />
            <MetricCard
              icon={AlertTriangle}
              label="Violations"
              value={orSoon(data.licensing.violations)}
              status={data.licensing.violations > 0 ? 'danger' : 'success'}
            />
            <MetricCard
              icon={TrendingUp}
              label="Trial Conversion"
              value={orSoon(data.licensing.trialConversionRate)}
              unit="%"
            />
          </div>
          <div className="metrics-grid" style={{ marginTop: 'var(--space-4)' }}>
            <MetricCard
              icon={AlertCircle}
              label="Suspended"
              value={data.licensing.suspended}
              status={data.licensing.suspended > 0 ? 'warning' : 'success'}
            />
            <MetricCard
              icon={AlertTriangle}
              label="License-to-PC Mismatches"
              value={orSoon(data.licensing.mismatches)}
              status={data.licensing.mismatches > 0 ? 'warning' : 'success'}
            />
          </div>
        </Section>

        {/* Section 7: Security & Integrity */}
        <Section icon={Shield} title="Security & Integrity">
          <div className="metrics-grid">
            <MetricCard
              icon={Users}
              label="Failed Logins (24h)"
              value={data.security.failedLogins24h}
              status={data.security.failedLoginsRate === 'normal' ? 'success' : data.security.failedLoginsRate === 'elevated' ? 'warning' : 'danger'}
            />
            <MetricCard
              icon={AlertCircle}
              label="OTP Failures"
              value={orSoon(data.security.otpFailures)}
              status={data.security.otpFailures < 5 ? 'success' : 'warning'}
            />
            <MetricCard
              icon={Shield}
              label="Token Anomalies"
              value={orSoon(data.security.tokenAnomalies)}
              status={data.security.tokenAnomalies === 0 ? 'success' : 'danger'}
            />
            <MetricCard
              icon={CheckCircle}
              label="Agent Integrity"
              value={hasValue(data.security.integrityOk) ? (data.security.integrityOk ? 'OK' : 'FAIL') : NO_DATA}
              status={data.security.integrityOk ? 'success' : 'danger'}
            />
          </div>
          <div className="metrics-grid" style={{ marginTop: 'var(--space-4)' }}>
            <MetricCard
              icon={Shield}
              label="Access Denied (24h)"
              value={data.security.loginDenied24h}
              hint="authenticated, wrong role"
              status={data.security.loginDenied24h === 0 ? 'success' : 'warning'}
            />
            <MetricCard
              icon={AlertCircle}
              label="2FA Failures (24h)"
              value={data.security.twofaFailures24h}
              status={data.security.twofaFailures24h === 0 ? 'success' : 'warning'}
            />
            <MetricCard
              icon={AlertTriangle}
              label="Account Lockouts (24h)"
              value={data.security.lockouts24h}
              status={data.security.lockouts24h === 0 ? 'success' : 'warning'}
            />
          </div>
        </Section>

        {/* Section 8: Background Jobs */}
        <Section icon={Clock} title="Background Jobs">
          <div className="metrics-grid metrics-grid--small">
            <MetricCard
              icon={Activity}
              label="Queue Depth"
              value={orSoon(data.jobs.queueDepth)}
              status={data.jobs.queueDepth > 100 ? 'warning' : 'success'}
            />
            <MetricCard
              icon={Play}
              label="Processing"
              value={orSoon(data.jobs.processing)}
              hint={hasValue(data.jobs.workers) ? `${data.jobs.workers} worker(s)` : null}
            />
            <MetricCard icon={XCircle} label="Failed (24h)" value={orSoon(data.jobs.failed24h)} status={data.jobs.failed24h > 0 ? 'warning' : 'success'} />
            <MetricCard icon={AlertTriangle} label="Long Running" value={orSoon(data.jobs.longRunning)} status={data.jobs.longRunning > 0 ? 'warning' : 'success'} />
          </div>
          <div className="jobs-list">
            <h4 className="subsection-title">Queues</h4>
            {data.jobs.queues.length === 0 ? (
              <div className="empty-state">Broker unreachable</div>
            ) : data.jobs.queues.map((q) => (
              <div key={q.name} className="job-row">
                <span className={`status-dot status-dot--${q.depth > 100 ? 'warning' : 'online'}`} />
                <span className="job-row__name">{q.name}</span>
                <span className="job-row__stats">
                  <span className={q.depth > 100 ? 'text-danger' : 'text-success'}>{q.depth} queued</span>
                </span>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* Section 9: Alerts & Insights */}
      <Section icon={Bell} title="Alerts & Insights">
        <div className="alerts-grid">
          <div className="alerts-column">
            <h4 className="subsection-title">Active Alerts</h4>
            {data.alerts.length === 0 ? (
              <div className="empty-state">No active alerts</div>
            ) : (
              data.alerts.map((alert) => (
                <div key={alert.id} className={`alert-card alert-card--${alert.severity}`}>
                  <div className="alert-card__icon">
                    {alert.severity === 'critical' && <XCircle size={18} />}
                    {alert.severity === 'warning' && <AlertTriangle size={18} />}
                    {alert.severity === 'info' && <Bell size={18} />}
                  </div>
                  <div className="alert-card__content">
                    <span className="alert-card__title">{alert.title}</span>
                    <span className="alert-card__time">{alert.category}</span>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="insights-column">
            <h4 className="subsection-title">Intelligent Insights</h4>
            {!data.insights.baselineReady ? (
              // "Still building a baseline" is NOT the same claim as "nothing is
              // wrong", so it must not render as an empty all-clear list. The
              // hourly rollup needs pointsNeeded samples before any comparison
              // means anything — say so, with progress.
              <div
                className="empty-state"
                title="An hourly trend point is recorded each hour; insights compare the newest against the median of the rest."
              >
                Building baseline
                {data.insights.pointsNeeded
                  ? ` — ${data.insights.points}/${data.insights.pointsNeeded} hourly samples`
                  : ''}
              </div>
            ) : data.insights.items.length === 0 ? (
              <div className="empty-state">No insights</div>
            ) : (
              data.insights.items.map((insight, idx) => (
                <div key={idx} className={`insight-card insight-card--${insight.severity || 'info'}`}>
                  <div className="insight-card__icon">
                    {insight.severity === 'critical' && <XCircle size={18} />}
                    {insight.severity === 'warning' && <AlertTriangle size={18} />}
                    {(!insight.severity || insight.severity === 'info') && <TrendingUp size={18} />}
                  </div>
                  <span className="insight-card__message">{insight.message}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </Section>

      {/* Section 10: System Actions (Super Admin Only) */}
      {canActAsSuperAdmin() && (
        <Section icon={Settings} title="System Actions">
          <div className="actions-grid">
            <button
              className="action-button"
              onClick={() => {
                if (confirm('Are you sure you want to restart the backend service?')) {
                  executeSystemAction('Restart Service', 'restart-service');
                }
              }}
              disabled={actionLoading === 'restart'}
            >
              <RefreshCw size={20} className={actionLoading === 'restart' ? 'spin' : ''} />
              <span>Restart Service</span>
            </button>
            <button
              className={`action-button ${data.systemState?.deployments_paused ? 'action-button--active' : ''}`}
              onClick={() => {
                if (confirm(`Are you sure you want to ${data.systemState?.deployments_paused ? 'resume' : 'pause'} deployments?`)) {
                  executeSystemAction('Pause Deployments', 'pause-deployments');
                }
              }}
              disabled={actionLoading === 'pause'}
            >
              <Pause size={20} />
              <span>{data.systemState?.deployments_paused ? 'Resume Deployments' : 'Pause Deployments'}</span>
            </button>
            <button
              className={`action-button ${data.systemState?.commands_disabled ? 'action-button--active' : ''}`}
              onClick={() => {
                if (confirm(`Are you sure you want to ${data.systemState?.commands_disabled ? 'enable' : 'disable'} PC commands?`)) {
                  executeSystemAction('Toggle Commands', 'disable-commands');
                }
              }}
              disabled={actionLoading === 'commands'}
            >
              <Power size={20} />
              <span>{data.systemState?.commands_disabled ? 'Enable Commands' : 'Disable Commands'}</span>
            </button>
            <button
              className="action-button"
              onClick={() => {
                if (confirm('Are you sure you want to force all PCs to re-handshake?')) {
                  executeSystemAction('Force Handshake', 'force-handshake');
                }
              }}
              disabled={actionLoading === 'handshake'}
            >
              <Activity size={20} />
              <span>Force Re-handshake</span>
            </button>
            <button
              className={`action-button ${data.systemState?.maintenance_mode ? 'action-button--danger' : 'action-button--warning'}`}
              onClick={() => {
                if (confirm(`Are you sure you want to ${data.systemState?.maintenance_mode ? 'disable' : 'enable'} maintenance mode?`)) {
                  handleMaintenanceMode();
                }
              }}
              disabled={actionLoading === 'maintenance'}
            >
              <AlertTriangle size={20} />
              <span>{data.systemState?.maintenance_mode ? 'Disable Maintenance' : 'Maintenance Mode'}</span>
            </button>
          </div>
          <p className="actions-note">All actions require confirmation and are logged for audit.</p>
        </Section>
      )}

      {/* Section 11: Audit Trail */}
      <Section icon={FileText} title="Audit & Observability">
        {/* Scroll wrapper, NOT .audit-timeline itself: the timeline's connector
            line is an absolutely-positioned ::before spanning its own height, so
            scrolling the timeline would clip the line to the visible window and
            leave it static while the entries moved past it. */}
        <div className="audit-scroll">
          <div className="audit-timeline">
            {data.auditTrail.length === 0 ? (
              <div className="empty-state">No recent activity</div>
            ) : data.auditTrail.map((event) => (
              <div key={event.id} className={`timeline-item timeline-item--${event.type}`}>
                <div className="timeline-item__dot" />
                <div className="timeline-item__content">
                  <span className="timeline-item__message">{event.message}</span>
                  <div className="timeline-item__meta">
                    <span>{formatTimeAgo(event.time)}</span>
                    {event.user && <span>by {event.user}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="audit-actions">
          <button className="btn btn--secondary btn--sm" onClick={exportAuditCsv} disabled={!data.auditTrail.length}>
            <Download size={14} />
            Export CSV
          </button>
          <button className="btn btn--secondary btn--sm" onClick={() => navigate('/audit-logs')}>
            <ExternalLink size={14} />
            View Full Logs
          </button>
        </div>
      </Section>

      {/* Styles */}
      <style>{`
        .system-health {
          /* noLag rebrand: scoped to this page only, same pattern as
             Sidebar/Header/Dashboard/Analytics. IMPORTANT: this only
             overrides --accent-primary (brand) — every health-status color
             on this page (--status-success/warning/danger/info) is a
             SEPARATE token, untouched by this scoping. Figma's mockup
             shows every metric card with the same flat orange left-border
             because Figma has no live data to color by; the real health
             logic below (.metric-card--success/warning/danger etc.) must
             keep driving that border, not this accent var. */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);

          max-width: 1600px;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: var(--space-6);
        }

        .page-header-content {
          display: flex;
          align-items: center;
          gap: var(--space-4);
        }

        .page-header-icon {
          width: 48px;
          height: 48px;
          border-radius: var(--radius-lg);
          background: var(--accent-gradient);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .page-title {
          font-size: var(--text-2xl);
          font-weight: 700;
          margin-bottom: var(--space-1);
        }

        .page-subtitle {
          font-size: var(--text-sm);
          color: var(--text-tertiary);
        }

        /* Global Health Banner */
        .global-health {
          /* Figma-confirmed white-sheen recipe (get_design_context on
             2545:1915), same as every other card on this page. This one
             has its own backdrop-filter (it sits directly on the page
             background, not nested), so it needs no dark base — unlike
             the nested tiles below. */
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.07) 45%, rgba(255, 255, 255, 0.04) 100%);
          backdrop-filter: blur(14px) saturate(180%);
          -webkit-backdrop-filter: blur(14px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 16px;
          box-shadow: inset 0 1px 0.5px rgba(255, 255, 255, 0.45), 0 20px 48px rgba(0, 0, 0, 0.5);
          padding: var(--space-6);
          margin-bottom: var(--space-6);
          display: flex;
          align-items: center;
          gap: var(--space-8);
        }

        .global-health__status {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: var(--space-2);
        }

        .global-status-badge {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-3) var(--space-5);
          border-radius: var(--radius-lg);
          font-size: var(--text-lg);
          font-weight: 700;
          letter-spacing: 0.05em;
        }

        .global-status-badge--healthy {
          background: var(--status-success-subtle);
          color: var(--status-success);
        }

        .global-status-badge--degraded {
          background: var(--status-warning-subtle);
          color: var(--status-warning);
        }

        .global-status-badge--incident {
          background: var(--status-danger-subtle);
          color: var(--status-danger);
        }

        .global-health__live-indicator {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          font-size: var(--text-xs);
          color: var(--text-tertiary);
        }

        .live-dot {
          width: 8px;
          height: 8px;
          background: var(--status-success);
          border-radius: 50%;
          animation: pulse 2s ease-in-out infinite;
        }

        .global-health__metrics {
          display: flex;
          flex: 1;
          gap: var(--space-8);
        }

        .global-metric {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .global-metric__value {
          font-size: var(--text-2xl);
          font-weight: 700;
          font-family: var(--font-mono);
          color: var(--text-primary);
        }

        .global-metric__label {
          font-size: var(--text-xs);
          color: var(--text-tertiary);
          margin-top: var(--space-1);
        }

        /* Health Grid */
        .health-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--space-6);
          margin-bottom: var(--space-6);
        }

        @media (max-width: 1200px) {
          .health-grid {
            grid-template-columns: 1fr;
          }
        }

        /* Section */
        .health-section {
          /* Also sits directly on the page background (not nested) — own
             backdrop-filter, no dark base needed. */
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.07) 45%, rgba(255, 255, 255, 0.04) 100%);
          backdrop-filter: blur(14px) saturate(180%);
          -webkit-backdrop-filter: blur(14px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 16px;
          box-shadow: inset 0 1px 0.5px rgba(255, 255, 255, 0.45), 0 20px 48px rgba(0, 0, 0, 0.5);
          overflow: hidden;
        }

        .health-section__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--space-5);
          border-bottom: 1px solid var(--divider);
        }

        .health-section__title-group {
          display: flex;
          align-items: center;
          gap: var(--space-3);
        }

        .health-section__icon {
          color: var(--accent-primary);
        }

        .health-section__title {
          font-size: var(--text-base);
          font-weight: 600;
        }

        .health-section__badge {
          background: var(--accent-primary-subtle);
          color: var(--accent-primary);
          padding: var(--space-1) var(--space-2);
          border-radius: var(--radius-sm);
          font-size: var(--text-xs);
          font-weight: 500;
        }

        .health-section__chevron {
          color: var(--text-tertiary);
          transition: transform var(--duration-fast);
        }

        .health-section__chevron.rotated {
          transform: rotate(90deg);
        }

        .health-section__content {
          padding: var(--space-5);
        }

        /* Metrics Grid — auto-fit, not a fixed 4-column + viewport-width
           media query. .health-grid above splits into 2 columns once the
           viewport is wide enough, so each .health-section only ever gets
           roughly HALF the viewport width — the old media query checked
           viewport width, not the section's actual (much narrower)
           available width, so at e.g. 1300px viewport it stayed 4-column
           while each section had only ~600px to fit them in, clipping the
           4th card almost entirely. auto-fit sizes off the real container
           width instead, so it reflows correctly regardless of whether
           .health-grid is 1 or 2 columns. */
        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: var(--space-4);
        }

        .metrics-grid--small {
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
        }

        /* Metric Card */
        .metric-card {
          /* No backdrop-filter here on purpose: this card is nested INSIDE
             .health-section, which already applies blur(14px) saturate(180%)
             to the page background behind it. A second saturate(180%) here
             would sample the section's already-boosted backdrop and
             compound to ~324% — the blown-out purple/magenta look. Figma's
             static blur doesn't compound like nested CSS backdrop-filter
             does, so this only shows up once actually rendered. The
             gradient fill alone still reads as glass against the section's
             own blur. */
          /* Dark base UNDER the sheen — without it (gradient bottoms out at
             4% white, essentially transparent) each tile just shows
             whichever page-background blob happens to sit behind it,
             which is why some tiles read neutral gray and others read
             purple depending on position. The top-level cards don't have
             this problem because their own backdrop-filter blurs the
             backdrop into a uniform smear first; these nested tiles have
             no backdrop-filter of their own (see above), so they need an
             opaque-ish base instead. */
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.07) 45%, rgba(255, 255, 255, 0.04) 100%), rgba(14, 14, 20, 0.55);
          /* Deliberately NOT a border shorthand: top/right/bottom get the
             white-sheen recipe, but border-left is left for
             .metric-card--success/warning/danger below to own — those set
             border-left-color to the real health status, and Figma's own
             flat-orange left border on every card (no live data to color
             by there) must not overwrite that signal. */
          border-top: 1px solid rgba(255, 255, 255, 0.18);
          border-right: 1px solid rgba(255, 255, 255, 0.18);
          border-bottom: 1px solid rgba(255, 255, 255, 0.18);
          border-left: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 16px;
          box-shadow: inset 0 1px 0.5px rgba(255, 255, 255, 0.45), 0 20px 48px rgba(0, 0, 0, 0.5);
          padding: var(--space-4);
          transition: all var(--duration-fast);
        }

        .metric-card:hover {
          border-color: rgba(255, 255, 255, 0.3);
        }

        .metric-card--success { border-left: 3px solid var(--status-success); }
        .metric-card--warning { border-left: 3px solid var(--status-warning); }
        .metric-card--danger { border-left: 3px solid var(--status-danger); }

        .metric-card__header {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          margin-bottom: var(--space-2);
        }

        .metric-card__icon {
          color: var(--text-tertiary);
        }

        .metric-card__label {
          font-size: var(--text-xs);
          color: var(--text-tertiary);
        }

        .metric-card__body {
          display: flex;
          align-items: baseline;
          gap: var(--space-1);
        }

        .metric-card__value {
          font-size: var(--text-xl);
          font-weight: 700;
          font-family: var(--font-mono);
        }

        .metric-card__unit {
          font-size: var(--text-sm);
          color: var(--text-tertiary);
        }

        .metric-card--pending { opacity: 0.72; }

        .metric-card__soon {
          font-size: var(--text-base);
          font-weight: 600;
          color: var(--text-quaternary, var(--text-tertiary));
          letter-spacing: 0.02em;
          cursor: help;
        }

        .metric-card__hint {
          font-size: var(--text-xs);
          color: var(--text-tertiary);
        }

        .global-metric__value--soon {
          font-size: var(--text-base);
          color: var(--text-quaternary, var(--text-tertiary));
          cursor: help;
        }

        .metric-card__footer {
          margin-top: var(--space-3);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .metric-card__trend {
          display: flex;
          align-items: center;
          gap: var(--space-1);
          font-size: var(--text-xs);
          font-weight: 500;
        }

        .metric-card__trend--up { color: var(--status-success); }
        .metric-card__trend--down { color: var(--status-danger); }

        .sparkline {
          width: 60px;
          opacity: 0.7;
        }

        /* Status Badge Inline */
        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: var(--space-1);
          padding: var(--space-1) var(--space-2);
          border-radius: var(--radius-sm);
          font-size: var(--text-xs);
          font-weight: 500;
        }

        .status-badge--success { background: var(--status-success-subtle); color: var(--status-success); }
        .status-badge--warning { background: var(--status-warning-subtle); color: var(--status-warning); }
        .status-badge--danger { background: var(--status-danger-subtle); color: var(--status-danger); }
        .status-badge--info { background: var(--status-info-subtle); color: var(--status-info); }

        /* Status Dot */
        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .status-dot--online { background: var(--status-success); }
        .status-dot--warning { background: var(--status-warning); }
        .status-dot--offline { background: var(--text-quaternary); }

        /* Services List */
        .services-list {
          margin-top: var(--space-5);
          border-top: 1px solid var(--divider);
          padding-top: var(--space-4);
        }

        .subsection-title {
          font-size: var(--text-xs);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--text-tertiary);
          margin-bottom: var(--space-3);
        }

        .service-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--space-2) 0;
        }

        .service-row:not(:last-child) {
          border-bottom: 1px solid var(--divider);
        }

        .service-row__status {
          display: flex;
          align-items: center;
          gap: var(--space-2);
        }

        .service-row__name {
          font-size: var(--text-sm);
        }

        .service-row__meta {
          display: flex;
          gap: var(--space-4);
          font-size: var(--text-xs);
          color: var(--text-tertiary);
        }

        .service-row__latency {
          font-family: var(--font-mono);
        }

        /* Backup Status */
        .backup-status {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: var(--space-4);
          padding-top: var(--space-4);
          border-top: 1px solid var(--divider);
        }

        .backup-badge {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-2) var(--space-3);
          border-radius: var(--radius-sm);
          font-size: var(--text-sm);
        }

        .backup-badge--success {
          background: var(--status-success-subtle);
          color: var(--status-success);
        }

        .backup-badge--failed {
          background: var(--status-danger-subtle);
          color: var(--status-danger);
        }

        .replication-lag {
          font-size: var(--text-xs);
          color: var(--text-tertiary);
        }

        /* Fleet Overview */
        .fleet-overview {
          display: flex;
          gap: var(--space-6);
          margin-bottom: var(--space-5);
        }

        .fleet-stat {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: var(--space-4);
          /* No backdrop-filter — nested inside .health-section, which
             already blurs/saturates the page background; see .metric-card
             above for why stacking a second one blows out the color. */
          /* Dark base UNDER the sheen — without it (gradient bottoms out at
             4% white, essentially transparent) each tile just shows
             whichever page-background blob happens to sit behind it,
             which is why some tiles read neutral gray and others read
             purple depending on position. The top-level cards don't have
             this problem because their own backdrop-filter blurs the
             backdrop into a uniform smear first; these nested tiles have
             no backdrop-filter of their own (see above), so they need an
             opaque-ish base instead. */
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.07) 45%, rgba(255, 255, 255, 0.04) 100%), rgba(14, 14, 20, 0.55);
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 16px;
          box-shadow: inset 0 1px 0.5px rgba(255, 255, 255, 0.45);
          flex: 1;
        }

        .fleet-stat__value {
          font-size: var(--text-2xl);
          font-weight: 700;
          font-family: var(--font-mono);
        }

        .fleet-stat--total .fleet-stat__value { color: var(--text-primary); }
        .fleet-stat--online .fleet-stat__value { color: var(--status-success); }
        .fleet-stat--offline .fleet-stat__value { color: var(--text-tertiary); }
        .fleet-stat--stale .fleet-stat__value { color: var(--status-warning); }

        .fleet-stat__label {
          font-size: var(--text-xs);
          color: var(--text-tertiary);
          margin-top: var(--space-1);
        }

        .fleet-stat__pct {
          font-size: var(--text-xs);
          color: var(--status-success);
          margin-top: var(--space-1);
        }

        /* Fleet Online History (big graph) */
        .fleet-history-card {
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.07) 45%, rgba(255, 255, 255, 0.04) 100%), rgba(14, 14, 20, 0.55);
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 16px;
          box-shadow: inset 0 1px 0.5px rgba(255, 255, 255, 0.45);
          padding: var(--space-4) var(--space-4) var(--space-2);
          margin-bottom: var(--space-4);
        }

        .fleet-history-card__header {
          margin-bottom: var(--space-3);
        }

        .fleet-history-card__subtitle {
          display: block;
          font-size: var(--text-xs);
          color: var(--text-tertiary);
          margin-top: var(--space-1);
        }

        /* Distribution Grid */
        .distribution-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: var(--space-4);
        }

        .distribution-card {
          /* No backdrop-filter — nested inside .health-section; see
             .metric-card for why stacking a second one blows out the color. */
          /* Dark base UNDER the sheen — without it (gradient bottoms out at
             4% white, essentially transparent) each tile just shows
             whichever page-background blob happens to sit behind it,
             which is why some tiles read neutral gray and others read
             purple depending on position. The top-level cards don't have
             this problem because their own backdrop-filter blurs the
             backdrop into a uniform smear first; these nested tiles have
             no backdrop-filter of their own (see above), so they need an
             opaque-ish base instead. */
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.07) 45%, rgba(255, 255, 255, 0.04) 100%), rgba(14, 14, 20, 0.55);
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 16px;
          box-shadow: inset 0 1px 0.5px rgba(255, 255, 255, 0.45);
          padding: var(--space-4);
        }

        .distribution-card__title {
          font-size: var(--text-xs);
          font-weight: 600;
          color: var(--text-tertiary);
          margin-bottom: var(--space-3);
        }

        .distribution-row {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          margin-bottom: var(--space-2);
        }

        .distribution-row__label {
          font-size: var(--text-xs);
          color: var(--text-secondary);
          width: 100px;
          flex-shrink: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .distribution-row__bar {
          flex: 1;
          height: 6px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 3px;
          overflow: hidden;
        }

        .distribution-row__bar > div {
          height: 100%;
          background: var(--accent-primary);
          border-radius: 3px;
        }

        .distribution-row__value {
          font-size: var(--text-xs);
          font-family: var(--font-mono);
          color: var(--text-tertiary);
          width: 40px;
          text-align: right;
        }

        /* Stale-PC rows (Offline 30+ Days card) carry a longer "#id name"
           label and a longer "45d ago" value than the count-distribution
           rows above, so they widen/flex those two columns instead of the
           fixed-width defaults built for short version/OS strings + counts. */
        .distribution-row__label--wide {
          width: auto;
          flex: 1 1 auto;
        }

        .distribution-row__value--wide {
          width: 64px;
          flex-shrink: 0;
        }

        .stale-pc-list {
          max-height: 168px;
          overflow-y: auto;
        }

        /* Licensing Alerts */
        .licensing-alerts {
          margin-top: var(--space-4);
          display: flex;
          flex-direction: column;
          gap: var(--space-2);
        }

        .alert-row {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-3);
          border-radius: var(--radius-sm);
          font-size: var(--text-sm);
        }

        .alert-row--warning {
          background: var(--status-warning-subtle);
          color: var(--status-warning);
        }

        .alert-row--danger {
          background: var(--status-danger-subtle);
          color: var(--status-danger);
        }

        /* Jobs List */
        .jobs-list {
          margin-top: var(--space-4);
          border-top: 1px solid var(--divider);
          padding-top: var(--space-3);
        }

        .job-row {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          padding: var(--space-2) 0;
        }

        .job-row__name {
          font-size: var(--text-sm);
          flex: 1;
        }

        .job-row__stats {
          display: flex;
          gap: var(--space-3);
          font-size: var(--text-xs);
        }

        /* Alerts Grid */
        .alerts-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--space-6);
        }

        .alerts-column, .insights-column {
          display: flex;
          flex-direction: column;
          gap: var(--space-3);
        }

        .alert-card {
          display: flex;
          align-items: flex-start;
          gap: var(--space-3);
          padding: var(--space-3);
          /* No backdrop-filter — nested inside .health-section; see
             .metric-card for why stacking a second one blows out the color. */
          /* Dark base UNDER the sheen — without it (gradient bottoms out at
             4% white, essentially transparent) each tile just shows
             whichever page-background blob happens to sit behind it,
             which is why some tiles read neutral gray and others read
             purple depending on position. The top-level cards don't have
             this problem because their own backdrop-filter blurs the
             backdrop into a uniform smear first; these nested tiles have
             no backdrop-filter of their own (see above), so they need an
             opaque-ish base instead. */
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.07) 45%, rgba(255, 255, 255, 0.04) 100%), rgba(14, 14, 20, 0.55);
          border-radius: 16px;
          box-shadow: inset 0 1px 0.5px rgba(255, 255, 255, 0.45);
          /* border-left stays a separate property (untouched) so
             .alert-card--critical/warning/info below keep owning it. */
          border-left: 3px solid transparent;
        }

        .alert-card--critical { border-left-color: var(--status-danger); }
        .alert-card--warning { border-left-color: var(--status-warning); }
        .alert-card--info { border-left-color: var(--status-info); }

        .alert-card__icon {
          flex-shrink: 0;
        }

        .alert-card--critical .alert-card__icon { color: var(--status-danger); }
        .alert-card--warning .alert-card__icon { color: var(--status-warning); }
        .alert-card--info .alert-card__icon { color: var(--status-info); }

        .alert-card__content {
          display: flex;
          flex-direction: column;
          gap: var(--space-1);
        }

        .alert-card__title {
          font-size: var(--text-sm);
        }

        .alert-card__time {
          font-size: var(--text-xs);
          color: var(--text-tertiary);
        }

        .insight-card {
          display: flex;
          align-items: flex-start;
          gap: var(--space-3);
          padding: var(--space-3);
          background: var(--accent-primary-subtle);
          border-radius: var(--radius-md);
        }

        /* Severity variants mirror .alert-card--* so the two columns of this
           section read as one language: same tokens, same left-border cue. */
        .insight-card + .insight-card { margin-top: var(--space-2); }
        .insight-card--critical { background: var(--status-danger-subtle, var(--accent-primary-subtle)); }
        .insight-card--warning { background: var(--status-warning-subtle, var(--accent-primary-subtle)); }

        .insight-card__icon {
          color: var(--accent-primary);
          flex-shrink: 0;
        }

        .insight-card--critical .insight-card__icon { color: var(--status-danger); }
        .insight-card--warning .insight-card__icon { color: var(--status-warning); }
        .insight-card--info .insight-card__icon { color: var(--status-info); }

        .insight-card__message {
          font-size: var(--text-sm);
          color: var(--text-secondary);
        }

        .empty-state {
          text-align: center;
          padding: var(--space-6);
          color: var(--text-tertiary);
          font-size: var(--text-sm);
        }

        /* Actions Grid */
        .actions-grid {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-3);
        }

        .action-button {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-3) var(--space-4);
          /* No backdrop-filter — nested inside .health-section; see
             .metric-card for why stacking a second one blows out the color. */
          /* Dark base UNDER the sheen — without it (gradient bottoms out at
             4% white, essentially transparent) each tile just shows
             whichever page-background blob happens to sit behind it,
             which is why some tiles read neutral gray and others read
             purple depending on position. The top-level cards don't have
             this problem because their own backdrop-filter blurs the
             backdrop into a uniform smear first; these nested tiles have
             no backdrop-filter of their own (see above), so they need an
             opaque-ish base instead. */
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.07) 45%, rgba(255, 255, 255, 0.04) 100%), rgba(14, 14, 20, 0.55);
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 16px;
          box-shadow: inset 0 1px 0.5px rgba(255, 255, 255, 0.45);
          color: var(--text-secondary);
          font-size: var(--text-sm);
          cursor: pointer;
          transition: all var(--duration-fast);
        }

        .action-button:hover {
          border-color: rgba(255, 255, 255, 0.3);
          color: var(--text-primary);
        }

        .action-button--warning {
          border-color: var(--status-warning);
          color: var(--status-warning);
        }

        .actions-note {
          margin-top: var(--space-4);
          font-size: var(--text-xs);
          color: var(--text-tertiary);
        }

        /* Audit Timeline */
        .audit-scroll {
          max-height: 420px;
          overflow-y: auto;
          margin-bottom: var(--space-4);
          /* Room for the scrollbar so it never sits on top of the entries. */
          padding-right: var(--space-2);
        }

        .audit-timeline {
          position: relative;
          padding-left: var(--space-6);
        }

        .audit-timeline::before {
          content: '';
          position: absolute;
          left: 3px;
          top: 8px;
          bottom: 8px;
          width: 2px;
          background: var(--divider);
        }

        .timeline-item {
          position: relative;
          padding-bottom: var(--space-4);
        }

        .timeline-item__dot {
          position: absolute;
          left: calc(-1 * var(--space-6) + 0px);
          top: 4px;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--text-tertiary);
        }

        .timeline-item--deploy .timeline-item__dot { background: var(--accent-primary); }
        .timeline-item--alert .timeline-item__dot { background: var(--status-warning); }
        .timeline-item--action .timeline-item__dot { background: var(--status-info); }
        .timeline-item--resolve .timeline-item__dot { background: var(--status-success); }

        .timeline-item__content {
          display: flex;
          flex-direction: column;
          gap: var(--space-1);
        }

        .timeline-item__message {
          font-size: var(--text-sm);
        }

        .timeline-item__meta {
          display: flex;
          gap: var(--space-3);
          font-size: var(--text-xs);
          color: var(--text-tertiary);
        }

        .audit-actions {
          display: flex;
          gap: var(--space-3);
        }
      `}</style>
    </div>
  );
}
